import { ReportData } from "interfaces/ReportData.interface";
import { buildMyStoreUrl } from "config/constants";

const REPORTS_ENDPOINT = buildMyStoreUrl("/api/store/reports");

const getCookie = (name: string): string | null => {
  try {
    const cookies = document.cookie?.split(";") ?? [];
    for (const cookie of cookies) {
      const [key, ...rest] = cookie.trim().split("=");
      if (key === name) return decodeURIComponent(rest.join("="));
    }
  } catch {
    // ignore cookie parsing errors
  }
  return null;
};

const getStoredValue = (name: string): string | null => {
  try {
    if (typeof sessionStorage !== "undefined") {
      const sessionValue = sessionStorage.getItem(name);
      if (sessionValue) return sessionValue;
    }
    if (typeof localStorage !== "undefined") {
      const localValue = localStorage.getItem(name);
      if (localValue) return localValue;
    }
  } catch {
    // ignore storage access errors
  }
  return getCookie(name);
};

const formatDate = (date: Date): string => {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const day = String(date.getDate()).padStart(2, "0");
  return `${year}-${month}-${day}`;
};

const getDefaultCulture = (): string => {
  if (typeof navigator !== "undefined" && navigator.language) {
    return navigator.language;
  }
  return "en-us";
};

const parseReportPayload = (payload: unknown): ReportData | null => {
  if (!payload || typeof payload !== "object") return null;
  return payload as ReportData;
};

export interface FetchReportsParams {
  storeList?: Array<string | number>;
  culture?: string;
  startDate?: string;
  endDate?: string;
}

export const fetchReports = async (
  params: FetchReportsParams = {}
): Promise<ReportData | null> => {
  try {
    const authToken = getCookie("authToken");
    const storedStore = getStoredValue("storeNumber");
    const storeList =
      params.storeList && params.storeList.length > 0
        ? params.storeList
        : storedStore
          ? [storedStore]
          : [];
    if (storeList.length === 0) return null;

    const startDate = params.startDate || formatDate(new Date());
    const endDate = params.endDate || formatDate(new Date());
    const culture = params.culture || getDefaultCulture();

    const query = new URLSearchParams();
    storeList.map(String).forEach((store) => query.append("storeList", store));
    query.set("culture", culture);
    query.set("startDate", startDate);
    query.set("endDate", endDate);

    const response = await fetch(`${REPORTS_ENDPOINT}?${query.toString()}`, {
      method: "GET",
      headers: {
        ...(authToken ? { Authorization: `Bearer ${authToken}` } : {}),
      },
    });

    if (!response.ok) {
      console.error("Reports API error:", response.status, response.statusText);
      return null;
    }

    const data = await response.json();
    const first = Array.isArray(data) ? data[0] : data;
    return parseReportPayload(first);
  } catch (error) {
    console.error("Error fetching reports:", error);
    return null;
  }
};
